import { formatDistanceToNow } from 'date-fns';
import { UserNotification } from '../../hooks/useNotifications';
import { useNotifications } from '../../hooks/useNotifications';
import { cn } from '../../lib/utils';
import { ExternalLink } from 'lucide-react';

interface NotificationItemProps {
  notification: UserNotification;
  onClose?: () => void;
}

export function NotificationItem({ notification, onClose }: NotificationItemProps) {
  const { markAsRead } = useNotifications();

  const handleClick = async () => {
    // Mark as read
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Navigate to action URL if provided
    if (notification.actionUrl) {
      window.location.href = notification.actionUrl;
    }

    // Close the dropdown
    onClose?.();
  };

  const formattedTime = formatDistanceToNow(new Date(notification.sentAt), {
    addSuffix: true,
  });

  return (
    <button
      onClick={handleClick}
      className={cn(
        'w-full text-left px-4 py-3 hover:bg-accent transition-colors',
        !notification.read && 'bg-blue-50/50 dark:bg-blue-950/20'
      )}
    >
      <div className="flex items-start gap-3">
        {/* Unread indicator */}
        {!notification.read && (
          <div className="h-2 w-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
        )}

        <div className="flex-1 min-w-0">
          {/* Title */}
          <div className="flex items-start justify-between gap-2">
            <h4 className={cn(
              'text-sm leading-tight',
              !notification.read ? 'font-semibold' : 'font-medium'
            )}>
              {notification.title}
            </h4>
            {notification.actionUrl && (
              <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            )}
          </div>

          {/* Content */}
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
            {notification.content}
          </p>

          {/* Timestamp and Category */}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground">
              {formattedTime}
            </span>
            {notification.category && notification.category !== 'general' && (
              <>
                <span className="text-xs text-muted-foreground">•</span>
                <span className="text-xs text-muted-foreground capitalize">
                  {notification.category}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

